import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { appNoticeApi } from "../../services/appNotice.api";
import { AppNoticeDto } from "../../types/appNotice.definition";
import {
  createAppNoticeFailure,
  createAppNoticeRequest,
  createAppNoticeSuccess,
  deleteAppNoticeFailure,
  deleteAppNoticeRequest,
  deleteAppNoticeSuccess,
  fetchAppNoticesFailure,
  fetchAppNoticesRequest,
  fetchAppNoticesSuccess,
  updateAppNoticeFailure,
  updateAppNoticeRequest,
  updateAppNoticeSuccess,
} from "../reducers/appNoticeSlice";

// Helper to extract an error code from rejected interceptor errors.
function extractErrorCode(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as Record<string, unknown>).code === "string"
  ) {
    return (error as Record<string, unknown>).code as string;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "UNKNOWN_ERROR";
}

function* fetchAppNoticesSaga(
  action: ReturnType<typeof fetchAppNoticesRequest>
) {
  try {
    const { featureType } = action.payload;
    const data: AppNoticeDto[] = yield call(
      appNoticeApi.getAppNotices,
      featureType
    );
    yield put(
      fetchAppNoticesSuccess({ data, featureType: featureType ?? "ALL" })
    );
  } catch (error) {
    yield put(fetchAppNoticesFailure(extractErrorCode(error)));
  }
}

function* createAppNoticeSaga(
  action: ReturnType<typeof createAppNoticeRequest>
) {
  const { data, onSuccess, onError } = action.payload;
  try {
    const rule: AppNoticeDto = yield call(appNoticeApi.createAppNotice, data);
    yield put(createAppNoticeSuccess(rule));
    if (onSuccess) onSuccess(rule);
  } catch (error) {
    const code = extractErrorCode(error);
    yield put(createAppNoticeFailure(code));
    if (onError) onError(code);
  }
}

function* updateAppNoticeSaga(
  action: ReturnType<typeof updateAppNoticeRequest>
) {
  const { id, data, onSuccess, onError } = action.payload;
  try {
    const rule: AppNoticeDto = yield call(
      appNoticeApi.updateAppNotice,
      id,
      data
    );
    yield put(updateAppNoticeSuccess(rule));
    if (onSuccess) onSuccess(rule);
  } catch (error) {
    const code = extractErrorCode(error);
    yield put(updateAppNoticeFailure(code));
    if (onError) onError(code);
  }
}

function* deleteAppNoticeSaga(
  action: ReturnType<typeof deleteAppNoticeRequest>
) {
  const { id, onSuccess, onError } = action.payload;
  try {
    yield call(appNoticeApi.deleteAppNotice, id);
    yield put(deleteAppNoticeSuccess(id));
    if (onSuccess) onSuccess();
  } catch (error) {
    const code = extractErrorCode(error);
    yield put(deleteAppNoticeFailure(code));
    if (onError) onError(code);
  }
}

export function* appNoticeSaga() {
  yield takeLatest(fetchAppNoticesRequest, fetchAppNoticesSaga);
  yield takeEvery(createAppNoticeRequest, createAppNoticeSaga);
  yield takeEvery(updateAppNoticeRequest, updateAppNoticeSaga);
  yield takeEvery(deleteAppNoticeRequest, deleteAppNoticeSaga);
}
